import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Edit3, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

export default function MotosPage() {
    const [motos, setMotos] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        cliente_id: "",
        marca: "",
        modelo: "",
        num_chassis: "",
        num_motor: "",
        data_entrega: "",
    });
    const [saving, setSaving] = useState(false);

    async function load() {
        const [mt, cl] = await Promise.all([api.get("/motos"), api.get("/clientes")]);
        setMotos(mt.data);
        setClientes(cl.data);
    }
    useEffect(() => {
        load();
    }, []);

    const clienteById = useMemo(() => Object.fromEntries(clientes.map((c) => [c.id, c])), [clientes]);

    function openNew() {
        setEditing(null);
        setForm({ cliente_id: "", marca: "", modelo: "", num_chassis: "", num_motor: "", data_entrega: "" });
        setOpen(true);
    }
    function openEdit(m) {
        setEditing(m);
        setForm({
            cliente_id: m.cliente_id,
            marca: m.marca,
            modelo: m.modelo,
            num_chassis: m.num_chassis || "",
            num_motor: m.num_motor || "",
            data_entrega: m.data_entrega || "",
        });
        setOpen(true);
    }
    async function save() {
        if (!form.cliente_id || !form.marca.trim() || !form.modelo.trim()) {
            toast.error("Cliente, marca e modelo são obrigatórios.");
            return;
        }
        setSaving(true);
        try {
            if (editing) {
                await api.put(`/motos/${editing.id}`, form);
                toast.success("Moto atualizada.");
            } else {
                await api.post("/motos", form);
                toast.success("Moto criada.");
            }
            setOpen(false);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
        } finally {
            setSaving(false);
        }
    }
    async function remove(m) {
        if (!window.confirm(`Eliminar a moto ${m.marca} ${m.modelo}? Os serviços associados também serão removidos.`)) return;
        try {
            await api.delete(`/motos/${m.id}`);
            toast.success("Moto eliminada.");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
        }
    }

    const filtered = motos.filter((m) => {
        const cn = clienteById[m.cliente_id]?.nome || "";
        return [m.marca, m.modelo, m.num_chassis, m.num_motor, cn]
            .join(" ")
            .toLowerCase()
            .includes(q.toLowerCase());
    });

    return (
        <>
            <PageHeader
                eyebrow="Registo"
                title="Motos"
                description="Motas registadas na oficina, associadas a um proprietário."
                testid="motos-header"
                actions={
                    <Button
                        onClick={openNew}
                        className="rounded-sm bg-[#FF3B30] hover:bg-[#D63026]"
                        data-testid="new-moto-btn"
                        disabled={clientes.length === 0}
                    >
                        <Plus className="mr-2 h-4 w-4" /> Nova moto
                    </Button>
                }
            />

            {clientes.length === 0 ? (
                <div className="mb-4 rounded-sm border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    Registe primeiro um cliente antes de adicionar motas.
                </div>
            ) : null}

            <div className="mb-4 flex max-w-md items-center gap-2 border border-zinc-200 bg-white px-3">
                <Search className="h-4 w-4 text-zinc-400" />
                <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Procurar por marca, modelo, chassis..."
                    className="h-10 border-0 px-0 shadow-none focus-visible:ring-0"
                    data-testid="motos-search-input"
                />
            </div>

            <div className="border border-zinc-200 bg-white" data-testid="motos-table">
                <div className="grid grid-cols-12 gap-2 border-b border-zinc-200 bg-zinc-50 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">
                    <div className="col-span-3">Marca / Modelo</div>
                    <div className="col-span-3">Proprietário</div>
                    <div className="col-span-2">Nº Chassis</div>
                    <div className="col-span-2">Nº Motor</div>
                    <div className="col-span-1">Entrega</div>
                    <div className="col-span-1 text-right">Ações</div>
                </div>
                {filtered.length === 0 ? (
                    <div className="px-4 py-10 text-center text-sm text-zinc-500">
                        Sem motos registadas.
                    </div>
                ) : (
                    filtered.map((m) => (
                        <div
                            key={m.id}
                            data-testid={`moto-row-${m.id}`}
                            className="grid grid-cols-12 items-center gap-2 border-b border-zinc-100 px-4 py-3 text-sm hover:bg-zinc-50"
                        >
                            <div className="col-span-3 font-medium text-zinc-900">
                                {m.marca}
                                <div className="text-xs text-zinc-500">{m.modelo}</div>
                            </div>
                            <div className="col-span-3 text-zinc-700">
                                {clienteById[m.cliente_id]?.nome || "—"}
                            </div>
                            <div className="col-span-2 font-mono text-xs text-zinc-700">{m.num_chassis || "—"}</div>
                            <div className="col-span-2 font-mono text-xs text-zinc-700">{m.num_motor || "—"}</div>
                            <div className="col-span-1 text-xs text-zinc-700">{m.data_entrega || "—"}</div>
                            <div className="col-span-1 flex justify-end gap-1">
                                <Button
                                    size="icon"
                                    variant="outline"
                                    className="h-7 w-7 rounded-sm"
                                    onClick={() => openEdit(m)}
                                    data-testid={`moto-edit-${m.id}`}
                                >
                                    <Edit3 className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    size="icon"
                                    variant="outline"
                                    className="h-7 w-7 rounded-sm text-red-600 hover:text-red-700"
                                    onClick={() => remove(m)}
                                    data-testid={`moto-delete-${m.id}`}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-sm" data-testid="moto-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-['Outfit']">
                            {editing ? "Editar moto" : "Nova moto"}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                                Proprietário *
                            </Label>
                            <Select
                                value={form.cliente_id}
                                onValueChange={(v) => setForm({ ...form, cliente_id: v })}
                            >
                                <SelectTrigger className="mt-1.5 h-10 rounded-sm" data-testid="moto-cliente-select">
                                    <SelectValue placeholder="Selecionar cliente" />
                                </SelectTrigger>
                                <SelectContent>
                                    {clientes.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>
                                            {c.nome}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                                Marca *
                            </Label>
                            <Input
                                value={form.marca}
                                onChange={(e) => setForm({ ...form, marca: e.target.value })}
                                className="mt-1.5 h-10 rounded-sm"
                                data-testid="moto-marca-input"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                                Modelo *
                            </Label>
                            <Input
                                value={form.modelo}
                                onChange={(e) => setForm({ ...form, modelo: e.target.value })}
                                className="mt-1.5 h-10 rounded-sm"
                                data-testid="moto-modelo-input"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                                Nº Chassis
                            </Label>
                            <Input
                                value={form.num_chassis}
                                onChange={(e) => setForm({ ...form, num_chassis: e.target.value })}
                                className="mt-1.5 h-10 rounded-sm font-mono"
                                data-testid="moto-chassis-input"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                                Nº Motor
                            </Label>
                            <Input
                                value={form.num_motor}
                                onChange={(e) => setForm({ ...form, num_motor: e.target.value })}
                                className="mt-1.5 h-10 rounded-sm font-mono"
                                data-testid="moto-motor-input"
                            />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                                Data de entrega
                            </Label>
                            <Input
                                type="date"
                                value={form.data_entrega}
                                onChange={(e) => setForm({ ...form, data_entrega: e.target.value })}
                                className="mt-1.5 h-10 rounded-sm"
                                data-testid="moto-data-entrega-input"
                            />
                        </div>
                    </div>

                    <DialogFooter className="gap-2">
                        <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={save}
                            disabled={saving}
                            data-testid="moto-save-btn"
                            className="rounded-sm bg-[#FF3B30] hover:bg-[#D63026]"
                        >
                            {saving ? "A guardar..." : "Guardar"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
